import React from 'react';
import { createRoot } from 'react-dom/client';
import './styles/index.css';
import './styles/grid.css';
import './styles/utils.css';
import './styles/print.css';
import  { App } from './App';
import { BrowserRouter, useNavigate } from "react-router-dom";
import 'remixicon/fonts/remixicon.css';
import { Auth0Provider } from "@auth0/auth0-react";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { clearPendingLoginReturnTo } from './utils/auth0LoginReturnService';

const clientId = process.env.REACT_APP_CLIENT_ID || '';
const domain = process.env.REACT_APP_AUTH0_DOMAIN || '';
const queryClient = new QueryClient();

const defaultReturnTo = '/logged-in';

// Auth0 stores appState in its transaction cache.  Still validate it before
// navigating so that a malformed value can never send a user off-site.
const getSafeReturnTo = (returnTo?: string) => {
  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
    return defaultReturnTo;
  }

  return returnTo;
};

const Auth0ProviderWithRouter = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      useRefreshTokens={true}
      cacheLocation="localstorage"
      authorizationParams={{
        audience: process.env.REACT_APP_API_AUDIENCE,
        redirect_uri: `${window.location.origin}/logged-in`,
      }}
      onRedirectCallback={(appState) => {
        clearPendingLoginReturnTo();
        navigate(getSafeReturnTo(appState?.returnTo), { replace: true });
      }}>
      {children}
    </Auth0Provider>
  );
};

const root = createRoot(document.getElementById('root')!);
root.render(
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <Auth0ProviderWithRouter>
          <App />
        </Auth0ProviderWithRouter>
      </QueryClientProvider>
    </BrowserRouter>);
